import { getGlobalSettings } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import dayjs from 'dayjs';
import { getBoxRateField, useGetBoxRentalPriceString } from 'entities/Box';
import { ContractPreviewModal, getVatRateAmount, UnitType, useGetContractPreviewMutation } from 'entities/Contract';
import { addPercent, calculateDiscountValues } from 'entities/Discount';
import { getInsuranceRateField } from 'entities/Insurance';
import { getDepositAmount } from 'entities/Invoice';
import { useTakeUpReservationMutation } from 'entities/Reservation';
import { UserAccountType } from 'entities/User';
import { useGetVatRateQuery } from 'entities/VatRate';
import { FC, memo, useCallback, useState } from 'react';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { getTakeUpReservationState } from '../model/selectors/getTakeUpReservationState';
import { takeUpReservationModalActions } from '../model/slice/takeUpReservationModalSlice';

export const TakeUpReservationModal: FC = memo(() => {
  const dispatch = useAppDispatch();

  const { t } = useAppTranslation(['contracts', 'common']);

  const { reservation } = useAppSelector(getTakeUpReservationState);
  const globalSettings = useAppSelector(getGlobalSettings);

  const [isContractPreviewOpened, setContractPreviewOpened] = useState<boolean>(false);

  const [takeUpReservation, { isLoading }] = useTakeUpReservationMutation();
  const [getContractPreview, { data, isLoading: getContractPreviewLoading }] = useGetContractPreviewMutation();

  const box = reservation?.boxes[0] || null;

  const userAccountType = reservation?.user.accountType || UserAccountType.PERSONAL;

  const boxVatRateParams = {
    accountType: userAccountType,
    unitType: UnitType.BOX,
  };

  const insuranceVatRateParams = {
    accountType: userAccountType,
    unitType: UnitType.INSURANCE,
  };

  const { data: boxVatRate } = useGetVatRateQuery(boxVatRateParams);
  const { data: insuranceVatRate } = useGetVatRateQuery(insuranceVatRateParams);

  const rentalCost = useGetBoxRentalPriceString(box, reservation?.invoiceFrequencyType);

  const openContractPreview = useCallback(async (): Promise<void> => {
    if (reservation && box && rentalCost && globalSettings) {
      const { startRentDate, endRentDate, invoiceFrequencyType, user, paymentFrequencyPeriod, warehouse } = reservation;

      const minRentalPeriod = dayjs(endRentDate).diff(startRentDate, invoiceFrequencyType);

      const payForEntirePeriod = paymentFrequencyPeriod === 'entire period';

      const insurance = box?.insurance;
      const insuranceRateField = getInsuranceRateField(invoiceFrequencyType);
      const insuranceRate = insurance?.[insuranceRateField] || 0;
      const insuranceAmount = insuranceRate * (payForEntirePeriod ? minRentalPeriod : 1);

      const insuranceVatRatePercent = insuranceVatRate?.ratePercent || 0;
      const insuranceVatRateAmount = getVatRateAmount(insuranceAmount, insuranceVatRatePercent);
      const insuranceAmountWithVat = roundNumber(Math.round((insuranceAmount + insuranceVatRateAmount) * 100) / 100);

      const boxRateField = getBoxRateField(invoiceFrequencyType);
      const rentalUnitPrice = (box[boxRateField] as number) * box.priceFactor;
      const vatRatePercent = boxVatRate?.ratePercent || 0;
      const discountsArray = box.discounts;

      const depositAmount = getDepositAmount(Boolean(globalSettings?.isDepositApplied), box) || 0;

      const contractDurationPeriodNumbers = Array.from({ length: minRentalPeriod }, (el, index) => index + 1);

      const {
        totalAmount: allServicePeriodsUnitPrice,
        totalDiscountPercent: averageDiscountPercent,
        totalDiscountAmount,
        contractPeriodsUnitPrices,
      } = calculateDiscountValues(rentalUnitPrice, discountsArray, minRentalPeriod, contractDurationPeriodNumbers);

      const firstServicePeriodUnitPrice = contractPeriodsUnitPrices[0]?.rate;
      const firstServicePeriodDiscountPercent = contractPeriodsUnitPrices[0]?.discountPercent;
      const firstServicePeriodDiscountAmount = contractPeriodsUnitPrices[0]?.discountAmount;

      const firstServicePeriodUnitPriceWithVat = addPercent(firstServicePeriodUnitPrice, vatRatePercent);
      const firstServicePeriodTotalPrice = firstServicePeriodUnitPriceWithVat + insuranceAmountWithVat;

      const allServicePeriodsUnitPriceWithVat = addPercent(allServicePeriodsUnitPrice, vatRatePercent);
      const totalInsurancePriceWithVat = insuranceAmountWithVat;
      const allServicePeriodsTotalPrice = allServicePeriodsUnitPriceWithVat + totalInsurancePriceWithVat;

      const vatRateAmount = roundNumber(
        getVatRateAmount(payForEntirePeriod ? allServicePeriodsUnitPrice : firstServicePeriodUnitPrice, vatRatePercent),
      );

      const discountPercent = payForEntirePeriod ? averageDiscountPercent : firstServicePeriodDiscountPercent;

      const discountAmount = payForEntirePeriod ? roundNumber(totalDiscountAmount / minRentalPeriod, 2) : firstServicePeriodDiscountAmount;

      const totalWithoutDeposit = payForEntirePeriod ? allServicePeriodsTotalPrice : firstServicePeriodTotalPrice;
      const totalToPay = roundNumber(depositAmount + totalWithoutDeposit);

      try {
        await getContractPreview({
          warehouseName: warehouse.name,
          warehouseAddress: warehouse.address,
          startDate: startRentDate,
          boxId: box.originalEntityId,
          boxNumber: box.name,
          boxSquare: box.square,
          invoiceFrequencyType,
          contractDuration: minRentalPeriod,
          discountPercent,
          clientFirstName: user.firstName,
          clientLastName: user.lastName,
          clientStreet: user.street,
          clientCountry: user.country,
          clientCity: user.city,
          clientHouse: user.house,
          clientApartment: user.apartment,
          clientZip: user.zip,
          language: user.language,
          clientEmail: user.email,
          clientPhone: user.phone,
          rentalCost,
          insuranceAmount,
          insuranceVatRateAmount,
          discountAmount,
          totalToPay,
          paymentFrequencyPeriod,
          vatRateAmount,
          currency: globalSettings?.currency,
          depositAmount,
          vatRatePercent,
          insuranceVatRatePercent,
        }).unwrap();
      } catch (error: CustomAny) {
        showNotification('error', t('Error'), t('Error when getting contract preview'));
        console.log(error);
      } finally {
        setContractPreviewOpened(true);
      }
    }
  }, [reservation, box, boxVatRate?.ratePercent, insuranceVatRate?.ratePercent, rentalCost, globalSettings, getContractPreview, t]);

  const closeContractPreview = useCallback((): void => {
    setContractPreviewOpened(false);
  }, []);

  const closeTakeUpReservationModal = useCallback((): void => {
    dispatch(takeUpReservationModalActions.openTakeUpReservationModal(null));
  }, [dispatch]);

  const handleTakeUpReservation = useCallback(async (): Promise<void> => {
    try {
      if (reservation) {
        await takeUpReservation(reservation.reservationId).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Contract has been successfully created', { ns: 'common' }));
      }
    } catch (error: CustomAny) {
      console.log(error);
      showNotification('error', t('Error', { ns: 'common' }), t('Error when creating contract', { ns: 'common' }));
    } finally {
      closeTakeUpReservationModal();
      closeContractPreview();
    }
  }, [reservation, takeUpReservation, t, closeTakeUpReservationModal, closeContractPreview]);

  return (
    <>
      <ConfirmModal
        isOpened={Boolean(reservation)}
        title={t('Enter into contract')}
        description={t('Are you sure you want to enter into contract?')}
        isLoading={isLoading}
        onOk={openContractPreview}
        onCancel={closeTakeUpReservationModal}
      />
      <ContractPreviewModal
        isOpened={isContractPreviewOpened}
        isLoading={getContractPreviewLoading}
        contractPreview={data}
        onOk={handleTakeUpReservation}
        onCancel={closeContractPreview}
      />
    </>
  );
});
